import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { CreateTileCard } from "./CreateTileCard/CreateTileCard";
import {
	ContentWithSidebar,
	Dialog,
	SearchInput,
	Toolbar
} from "client/Components";
import { CreateNewTileForm } from "./CreateNewTileForm";
import {
	allAppsCategory,
	categories,
	CategoryEntry,
	CategoryFilter
} from "./CategoryFilter";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import {
	AlertManager,
	useAlerts,
	useUserScopedKey
} from "client/Hooks";
import { AppStoreSidebarKey } from "./AppStore";
import {
	AvailableAppModel,
	AvailableAppsViewModel,
	fetchTiles
} from "client/Api/Workspace/workspace";
import { SpaceListItem } from "client/Api/Apps/AppStore/appStore";
import { AppCategory } from "client/Api/Apps/common";
import { contains, normalizeForSearch } from "client/Data/searching";
import { generateTrackingId } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AppCardList } from "./AppCardList";
import { AppStoreLayout } from "./AppStoreLayout";

type CreateNewTileProps = {
	targetTileGroupId: number | null;
	targetSpace: SpaceListItem | null;
	activeSpaceId: number | null;
	onBack: () => void;
	onClose: () => void;
	onIsBusyChanged: (value: boolean) => void;
	onRefreshWorkspace: () => Promise<void>;
	onSwitchWorkspaceToEditMode: () => void;
};

export const CreateNewTile = (props: CreateNewTileProps): ReactElement => {
	const {
		targetTileGroupId,
		targetSpace,
		activeSpaceId,
		onClose,
		onBack,
		onIsBusyChanged,
		onRefreshWorkspace,
		onSwitchWorkspaceToEditMode
	} = props;

	const [currentCategory, setCurrentCategory] = useState<CategoryEntry>(allAppsCategory);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [createTileId, setCreateTileId] = useState<number | null>(null);
	const [tilesInStore, setTilesInStore] = useState<readonly AvailableAppModel[]>([]);
	const [searchValue, setSearchValue] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid(),
	});

	const alertManager: AlertManager = useAlerts();
	const persistenceKey: string = useUserScopedKey(AppStoreSidebarKey);


	const tilesWhichCanBeCreated = useMemo(
		(): readonly AvailableAppModel[] => {
			return tilesInStore
				.filter((tile) => tile.tileCanBeConfigured);
		},
		[tilesInStore]
	);

	const filteredTiles = useMemo(
		(): readonly AvailableAppModel[]=> {
			return filterTilesBySearchTermAndCategory(
				tilesWhichCanBeCreated,
				searchValue.value,
				currentCategory.id
			);
		},
		[
			tilesWhichCanBeCreated,
			currentCategory.id,
			searchValue.value
		]
	);


	const handleTileClick = useCallback(
		(tileId: number): void => {
			setCreateTileId(tileId);
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			const getTiles = async (signal: AbortSignal) => {
				try {
					const result: AvailableAppsViewModel = await fetchTiles(signal);

					setTilesInStore(result.availableApps);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			};

			const cancellation = new AbortController();
			getTiles(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, setTilesInStore]
	);

	const availableCategories = useMemo(
		(): CategoryEntry[] => {
			const tilesCategories = new Set<AppCategory | null>(
				tilesWhichCanBeCreated
					.map((tile) => tile.appCategory)
			);

			return categories
				.filter((category) => tilesCategories.has(category.id) || !category.id);
		},
		[tilesWhichCanBeCreated]
	);

	const handleSearchChange = useCallback(
		(value: string, version: Guid): void =>
			setSearchValue({ value, version }),
		[]
	);

	if (createTileId) {
		return (
			<CreateNewTileForm
				installedAppId={createTileId}
				targetTileGroupId={targetTileGroupId}
				targetSpace={targetSpace}
				activeSpaceId={activeSpaceId}
				onBack={() => setCreateTileId(null)}
				onClose={onClose}
				onIsBusyChanged={onIsBusyChanged}
				onRefreshWorkspace={onRefreshWorkspace}
				onSwitchWorkspaceToEditMode={onSwitchWorkspaceToEditMode}
			/>
		);
	}

	return (
		<>
			<Dialog.Header
				hasBorder
				title={localize("_CreateTile")}
				onClose={onClose}
				onBack={onBack}
			/>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar trackingId="create-new-tile">
					<ContentWithSidebar.Sidebar persistentKeyName={persistenceKey}>
						<CategoryFilter
							items={availableCategories}
							value={currentCategory}
							onChange={setCurrentCategory}
						/>
					</ContentWithSidebar.Sidebar>
					<ContentWithSidebar.Content ref={ref} hasBackgroundColor>
						<AppStoreLayout>
							<AppStoreLayout.Header
								search={
									<SearchInput
										trackingId="create-new-tile-search"
										onChange={handleSearchChange}
										value={searchValue}
										placeholder={localize("_Search")}
									/>
								}
							/>

							<AppStoreLayout.CategoryName name={currentCategory.name} />

							{filteredTiles.length > 0
								? (
									<AppCardList trackingId="card-list">
										{filteredTiles.map((x): ReactElement => (
											<CreateTileCard
												trackingId={generateTrackingId("create-tile-card", `|${x.installedAppId}`)}
												key={x.installedAppId}
												tile={x}
												onClick={handleTileClick}
											/>
										))}
									</AppCardList>
								)
								: <p>{localize("_NoResultsFound")}</p>
							}
						</AppStoreLayout>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						onClick={onBack}
						text={localize("_Back")}
						trackingId="back-button"
						responsiveBehavior="hide"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						trackingId="close-button"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};

CreateNewTile.displayName = "CreateNewTile";

export function filterTilesBySearchTermAndCategory(
	tiles: readonly AvailableAppModel[],
	searchTerm: string,
	categoryId: AppCategory | null
): readonly AvailableAppModel[] {
	const normalizedName: string = normalizeForSearch(searchTerm);

	const tilesByName: readonly AvailableAppModel[] = tiles.filter(
		(tile) => contains(
			tile.name,
			normalizedName
		)
			|| tile.tags
				.some(t => contains(t.name, normalizedName))
	);

	if (!categoryId) {
		return tilesByName;
	}

	return tilesByName.filter(
		(tile) => tile.appCategory === categoryId
	);
}